// uiarc: animated-counter (https://uiarc.dev/r/animated-counter.json) — CSS Modules yerine Tailwind. Her basamak
// bir çarktır: sayının gittiği yönde döner (9 → 0 sayaç gibi), basamak sayısı değişince sütun genişliği yayla
// açılıp kapanır, ilk görünüşte (`animateOnView`) tüm basamaklar sıfırdan yükselir.
// Sapma: uiarc `Intl.NumberFormat` ile kendisi biçimler; burada biçimli METİN verilir (`text`, Türkçe yerel ayar:
// `.` binlik, `,` ondalık; `lib/format.ts` yardımcılarının çıktısı) ve basamaklar metinden ayrıştırılır. Böylece
// para birimi, yüzde ve Excel sayı kalıpları aynen korunur; çarklar yalnız rakamlarda döner.
import {
  AnimatePresence,
  animate,
  motion,
  useInView,
  useMotionValue,
  useReducedMotion,
  useTransform,
  type MotionValue,
} from 'motion/react'
import { useEffect, useRef, useState } from 'react'
import { numericValue, partsFor } from '@/lib/counter-parts'
import { motionTokens } from '@/lib/motion'
import { cn } from '@/lib/utils'

const DIGITS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9]
const reveal = { ...motionTokens.spring.smooth, visualDuration: motionTokens.duration.considered }
const presence = {
  initial: { width: 0, opacity: 0 },
  animate: { width: 'auto', opacity: 1 },
  exit: { width: 0, opacity: 0 },
}

/** Çarktaki bir basamak. Çark konumuna uzaklığı yerini ve görünürlüğünü belirler. */
function Glyph({ position, digit }: { position: MotionValue<number>; digit: number }) {
  const offset = useTransform(position, (current) => ((((digit - current) % 10) + 15) % 10) - 5)
  const y = useTransform(offset, (current) => `${current}em`)
  const opacity = useTransform(offset, (current) => Math.max(0, 1 - Math.abs(current)))
  const visibility = useTransform(offset, (current) =>
    Math.abs(current) >= 1 ? 'hidden' : 'visible',
  )
  const filter = useTransform(offset, (current) =>
    Math.abs(current) < 0.02 || Math.abs(current) >= 1
      ? 'none'
      : `blur(${(Math.abs(current) * motionTokens.blur.subtle).toFixed(2)}px)`,
  )
  return (
    <motion.span
      className="absolute inset-x-0 inset-y-(--feather) text-center"
      style={{ y, opacity, filter, visibility }}
    >
      {digit}
    </motion.span>
  )
}

/** Basamak çarkı. Tüm sayının gittiği yönde döner, 9 → 0 sarar. */
function Column({
  digit,
  direction,
  armed,
  delay,
  reduceMotion,
}: {
  digit: number
  direction: number
  armed: boolean
  delay: number
  reduceMotion: boolean
}) {
  const position = useMotionValue(armed ? 0 : digit)
  const wheel = useRef({ digit: armed ? 0 : digit, target: armed ? 0 : digit, revealed: !armed })
  useEffect(() => {
    const state = wheel.current
    if (armed || state.digit === digit) {
      if (!armed) state.revealed = true
      return
    }
    state.target +=
      direction < 0 && state.revealed
        ? -((state.digit - digit + 10) % 10)
        : (digit - state.digit + 10) % 10
    state.digit = digit
    if (reduceMotion) position.jump(state.target)
    else
      animate(
        position,
        state.target,
        state.revealed ? motionTokens.spring.smooth : { ...reveal, delay },
      )
    state.revealed = true
  }, [armed, delay, digit, direction, position, reduceMotion])
  return (
    <motion.span
      className="relative -my-(--feather) inline-block overflow-hidden [mask-image:linear-gradient(to_bottom,transparent,#000_calc(var(--feather)*1.5),#000_calc(100%-var(--feather)*1.5),transparent)] py-(--feather) [--feather:0.16em]"
      {...presence}
      transition={reduceMotion ? { duration: 0 } : motionTokens.spring.morph}
    >
      <span className="invisible">0</span>
      {DIGITS.map((item) => (
        <Glyph key={item} position={position} digit={item} />
      ))}
    </motion.span>
  )
}

interface AnimatedCounterProps {
  /** Biçimli sayı metni (Türkçe yerel ayar; `formatInteger`, `formatNumberPattern` çıktısı). */
  text: string
  /** Yön için sayısal değer; verilmezse metinden okunur. */
  value?: number
  /** Sayacın üstünde duran, değişince bulanıklıktan yükselen etiket. */
  label?: string
  /** İlk kez görünüme girince tüm basamakları sıfırdan döndürür. */
  animateOnView?: boolean
  className?: string
}

/** Rakamları çark gibi dönen sayaç. Ekran okuyucu düz metni okur. */
export function AnimatedCounter({
  text,
  value,
  label,
  animateOnView = false,
  className,
}: AnimatedCounterProps) {
  const ref = useRef<HTMLSpanElement>(null)
  const inView = useInView(ref, { once: true, amount: 0.6 })
  const reduceMotion = !!useReducedMotion()
  const amount = value ?? numericValue(text)
  const [previous, setPrevious] = useState(amount)
  const [direction, setDirection] = useState(1)
  if (!Object.is(amount, previous)) {
    setPrevious(amount)
    setDirection(amount >= previous || Number.isNaN(amount) || Number.isNaN(previous) ? 1 : -1)
  }
  const parts = partsFor(text)
  const armed = animateOnView && !inView
  return (
    <span
      ref={ref}
      data-slot="animated-counter"
      className={cn(
        'text-foreground relative inline-flex flex-col text-3xl [line-height:1] font-medium tracking-(--tracking-display) tabular-nums',
        className,
      )}
    >
      {label && (
        <span className="text-text-muted leading-body mb-2 text-xs font-normal">
          <span className="relative block">
            <AnimatePresence mode="popLayout" initial={false}>
              <motion.span
                key={label}
                className="inline-block"
                initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: '0.3em' }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, transition: { duration: motionTokens.duration.instant } }}
                transition={{
                  duration: motionTokens.duration.standard,
                  ease: motionTokens.ease.enter,
                }}
              >
                {label}
              </motion.span>
            </AnimatePresence>
          </span>
        </span>
      )}
      <span className="sr-only">{text}</span>
      <span className="inline-flex items-start whitespace-nowrap select-none" aria-hidden="true">
        <AnimatePresence initial={false}>
          {parts.map((part) =>
            'digit' in part ? (
              <Column
                key={part.key}
                digit={part.digit}
                direction={direction}
                armed={armed}
                delay={Math.min(part.order * motionTokens.stagger.item, 0.25)}
                reduceMotion={reduceMotion}
              />
            ) : (
              <motion.span
                key={part.key}
                className="inline-block overflow-x-clip whitespace-pre"
                {...presence}
                transition={reduceMotion ? { duration: 0 } : motionTokens.spring.morph}
              >
                {part.text}
              </motion.span>
            ),
          )}
        </AnimatePresence>
      </span>
    </span>
  )
}
